/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export const PRENIGMA_STREAMING_VOICE_TURN_EVIDENCE_SCHEMA =
  "prenigma.element_call.streaming_voice_turn_evidence.v1" as const;

export const PRENIGMA_STREAMING_VOICE_TURN_PROTOCOL_ID =
  "prenigma.collaboration.streaming_voice_turn_protocol.v1" as const;

export const PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE =
  "continuous_call_media_runtime" as const;

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
interface JsonObject {
  [key: string]: JsonValue;
}

export interface PrenigmaStreamingVoiceProviderResolution {
  auditRef: string;
  credentialRef: string;
  dataResidency: string;
  language: string;
  model: string;
  policyChecked: boolean;
  provider: string;
  voiceId?: string;
}

export interface PrenigmaStreamingVoiceRoomContext {
  agentResponseRef: string;
  agentTurnMatrixEventRef: string;
  agentTurnTimelineReadbackRef: string;
  finalTranscriptMatrixEventRef?: string;
  finalTranscriptSourceEventId?: string;
  finalTranscriptTimelineReadbackRef?: string;
  noTranscriptMatrixEventRef?: string;
  noTranscriptReason?: string;
  noTranscriptSourceEventId?: string;
  noTranscriptTimelineReadbackRef?: string;
  transcriptPersistence:
    | "persist_final"
    | "persist_redacted_final"
    | "no_transcript";
}

export interface PrenigmaStreamingVoiceBargeInReadback {
  bargeInAtMs: number;
  bargeInSourceEventRef: string;
  bargeInTimelineReadbackRef: string;
  obsoleteAudioSuppressed: boolean;
  obsoleteTtsStreamRef: string;
  obsoleteTurnCancelledAtMs: number;
  obsoleteTurnId: string;
  sourceEventReadbackVerified: boolean;
  supersedesTurnId: string;
  timelineReadbackVerified: boolean;
  ttsCancelAckRef: string;
  ttsCancelAcknowledged: boolean;
}

export type PrenigmaStreamingVoiceEvidenceSourceKind =
  | typeof PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE
  | "batch_voice_turn_probe"
  | "external_runtime_adapter"
  | "live_browser_harness";

export interface PrenigmaStreamingVoiceEvidenceProvenance {
  browserHarness: boolean;
  continuousCallMediaObserved: boolean;
  derivedFromBatchVoiceTurn: boolean;
  hostSlot: "streamingVoiceEvidence" | string;
  producer: string;
  sourceKind: PrenigmaStreamingVoiceEvidenceSourceKind;
  sourceOfTruth: string;
}

export interface PrenigmaContinuousMediaObservationReadback {
  binding: string;
  blockers: string[];
  browserHarness: false;
  continuousCallMediaObserved: true;
  derivedFromBatchVoiceTurn: false;
  evidenceSourceKind: typeof PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE;
  hostSlot: "streamingVoiceEvidence" | string;
  mediaKind: "audio" | string;
  mediaRuntime: "livekit" | string;
  mode: string;
  producer: "element_call_collaboration_runtime" | string;
  providerCredentialsReturned: false;
  proved: true;
  rawAudioReturned: false;
  rawEventContentReturned: false;
  rawSecretMaterialReturned: false;
  rawTranscriptReturned: false;
  receiptEventId: string;
  schemaVersion: number;
  sourceEnvelopeSignatureVerified: true;
  sourceEventReadbackVerified: true;
  sourceEventRef: string;
  status: "proved" | string;
  trackReadyState: string;
  trackSource: "microphone" | string;
}

export interface BuildPrenigmaStreamingVoiceTurnEvidenceOptions {
  agentAckAtMs: number;
  agentFinalAtMs: number;
  bargeIn?: PrenigmaStreamingVoiceBargeInReadback;
  bridgeMetrics: {
    eventSourceToBridgeIngestMs: number;
    eventSourceToMatrixPersistMs: number;
  };
  continuousMediaObservation?: PrenigmaContinuousMediaObservationReadback;
  evidenceProvenance: PrenigmaStreamingVoiceEvidenceProvenance;
  liveProof: {
    authenticatedLiveSource: boolean;
    incrementalAgentDispatchObserved: boolean;
    matrixTimelineReadbackVerified: boolean;
    sourceEventReadbackVerified: boolean;
    streamingSttPartialObserved: boolean;
    streamingTtsAudioObserved: boolean;
  };
  productId: string;
  providerResolution: {
    stt: PrenigmaStreamingVoiceProviderResolution;
    tts: PrenigmaStreamingVoiceProviderResolution;
  };
  roomContext: PrenigmaStreamingVoiceRoomContext;
  roomId: string;
  sessionId: string;
  startedAtMs: number;
  sttFinalAtMs: number;
  sttFirstPartialAtMs: number;
  tenantId: string;
  ttsCompletedAtMs: number;
  ttsFirstAudioAtMs: number;
  turnId: string;
  workspaceId: string;
  workflowId: string;
}

export interface PrenigmaStreamingVoiceTurnEvidence {
  authenticated: true;
  continuous_media_observation?: JsonObject;
  live_proof: Record<string, boolean>;
  provider_credentials_returned: false;
  raw_audio_returned: false;
  raw_credentials_returned: false;
  raw_secret_material_returned: false;
  raw_transcript_returned: false;
  schema: typeof PRENIGMA_STREAMING_VOICE_TURN_EVIDENCE_SCHEMA;
  streaming_voice_turn_trace: JsonObject;
}

const RAW_MATERIAL_KEYS = new Set([
  "api_key",
  "audio",
  "audio_base64",
  "audio_bytes",
  "audio_url",
  "authorization",
  "credentials",
  "matrix_event_content",
  "provider_secret",
  "raw_audio",
  "raw_secret_material",
  "raw_transcript",
  "secret",
  "source_event_content",
  "token",
  "transcript",
  "transcript_text",
]);

export function buildPrenigmaStreamingVoiceTurnEvidence(
  options: BuildPrenigmaStreamingVoiceTurnEvidenceOptions,
): PrenigmaStreamingVoiceTurnEvidence {
  rejectRawMaterial(options, "options");
  requireIdentity(options);
  requireMonotonicPhases(options);
  requireRoomContext(options.roomContext);
  requireLiveProof(options.liveProof);
  requireEvidenceProvenance(options.evidenceProvenance);
  requireContinuousMediaObservation(
    options.evidenceProvenance,
    options.continuousMediaObservation,
  );
  requireProviderResolution(options.providerResolution.stt, "stt");
  requireProviderResolution(options.providerResolution.tts, "tts");
  if (options.bargeIn !== undefined) requireBargeIn(options.bargeIn);

  const trace: JsonObject = {
    tenant_id: options.tenantId,
    product_id: options.productId,
    workspace_id: options.workspaceId,
    room_id: options.roomId,
    session_id: options.sessionId,
    turn_id: options.turnId,
    workflow_id: options.workflowId,
    transport: "streaming_stt_incremental_agent_streaming_tts",
    slo_profile: "interactive_voice_slo_v1",
    barge_in_measurement_profile: "voice_barge_in_v1",
    monitoring_profile: "collaboration_runtime_v1",
    timestamps_ms: {
      started_at_ms: options.startedAtMs,
      stt_first_partial_at_ms: options.sttFirstPartialAtMs,
      stt_final_at_ms: options.sttFinalAtMs,
      agent_ack_at_ms: options.agentAckAtMs,
      agent_final_at_ms: options.agentFinalAtMs,
      tts_first_audio_at_ms: options.ttsFirstAudioAtMs,
      tts_completed_at_ms: options.ttsCompletedAtMs,
    },
    partial_transcript_persistence: "ephemeral",
    partial_intent_action_policy: "warm_only",
    partial_intent: {
      used_for: ["retrieval_warmup", "classification_warmup"],
    },
    consequential_actions_from_partial: false,
    provider_resolution: {
      stt: providerResolutionRecord(options.providerResolution.stt),
      tts: providerResolutionRecord(options.providerResolution.tts),
    },
    provider_fallback: {
      primary_failed: false,
      policy_checked: true,
      fallback_selected: false,
    },
    evidence_provenance: evidenceProvenanceRecord(options.evidenceProvenance),
    bridge_metrics: {
      event_source_to_bridge_ingest_ms:
        options.bridgeMetrics.eventSourceToBridgeIngestMs,
      event_source_to_matrix_persist_ms:
        options.bridgeMetrics.eventSourceToMatrixPersistMs,
    },
    room_context: roomContextRecord(options.roomContext),
  };

  if (options.bargeIn !== undefined) {
    Object.assign(trace, bargeInRecord(options.bargeIn));
  }

  const continuousMediaObservation =
    options.continuousMediaObservation !== undefined
      ? continuousMediaObservationRecord(options.continuousMediaObservation)
      : undefined;

  return {
    authenticated: true,
    ...(continuousMediaObservation !== undefined
      ? { continuous_media_observation: continuousMediaObservation }
      : {}),
    live_proof: {
      authenticated_live_source: options.liveProof.authenticatedLiveSource,
      incremental_agent_dispatch_observed:
        options.liveProof.incrementalAgentDispatchObserved,
      matrix_timeline_readback_verified:
        options.liveProof.matrixTimelineReadbackVerified,
      obsolete_audio_suppressed:
        options.bargeIn?.obsoleteAudioSuppressed === true,
      source_event_readback_verified:
        options.liveProof.sourceEventReadbackVerified,
      continuous_call_media_observed:
        options.evidenceProvenance.continuousCallMediaObserved === true,
      streaming_stt_partial_observed:
        options.liveProof.streamingSttPartialObserved,
      streaming_tts_audio_observed: options.liveProof.streamingTtsAudioObserved,
      tts_cancel_acknowledged: options.bargeIn?.ttsCancelAcknowledged === true,
    },
    provider_credentials_returned: false,
    raw_audio_returned: false,
    raw_credentials_returned: false,
    raw_secret_material_returned: false,
    raw_transcript_returned: false,
    schema: PRENIGMA_STREAMING_VOICE_TURN_EVIDENCE_SCHEMA,
    streaming_voice_turn_trace: trace,
  };
}

function evidenceProvenanceRecord(
  value: PrenigmaStreamingVoiceEvidenceProvenance,
): JsonObject {
  return {
    source_kind: value.sourceKind,
    producer: value.producer,
    host_slot: value.hostSlot,
    source_of_truth: value.sourceOfTruth,
    continuous_call_media_observed: value.continuousCallMediaObserved,
    derived_from_batch_voice_turn: value.derivedFromBatchVoiceTurn,
    browser_harness: value.browserHarness,
  };
}

function continuousMediaObservationRecord(
  value: PrenigmaContinuousMediaObservationReadback,
): JsonObject {
  return {
    schema_version: value.schemaVersion,
    mode: value.mode,
    binding: value.binding,
    status: value.status,
    proved: value.proved,
    continuous_call_media_observed: value.continuousCallMediaObserved,
    source_event_readback_verified: value.sourceEventReadbackVerified,
    source_envelope_signature_verified: value.sourceEnvelopeSignatureVerified,
    source_event_ref: value.sourceEventRef,
    receipt_event_id: value.receiptEventId,
    evidence_source_kind: value.evidenceSourceKind,
    host_slot: value.hostSlot,
    media_kind: value.mediaKind,
    media_runtime: value.mediaRuntime,
    producer: value.producer,
    track_source: value.trackSource,
    track_ready_state: value.trackReadyState,
    browser_harness: value.browserHarness,
    derived_from_batch_voice_turn: value.derivedFromBatchVoiceTurn,
    raw_audio_returned: value.rawAudioReturned,
    raw_transcript_returned: value.rawTranscriptReturned,
    provider_credentials_returned: value.providerCredentialsReturned,
    raw_event_content_returned: value.rawEventContentReturned,
    raw_secret_material_returned: value.rawSecretMaterialReturned,
    blockers: value.blockers,
  };
}

function providerResolutionRecord(
  value: PrenigmaStreamingVoiceProviderResolution,
): JsonObject {
  const record: JsonObject = {
    provider: value.provider,
    model: value.model,
    credential_ref: value.credentialRef,
    language: value.language,
    data_residency: value.dataResidency,
    policy_checked: value.policyChecked,
    audit_ref: value.auditRef,
  };
  if (value.voiceId !== undefined) record.voice_id = value.voiceId;
  return record;
}

function roomContextRecord(
  value: PrenigmaStreamingVoiceRoomContext,
): JsonObject {
  const record: JsonObject = {
    transcript_persistence: value.transcriptPersistence,
    agent_turn_matrix_event_ref: value.agentTurnMatrixEventRef,
    agent_turn_timeline_readback_ref: value.agentTurnTimelineReadbackRef,
    agent_response_ref: value.agentResponseRef,
  };
  addOptional(
    record,
    "final_transcript_matrix_event_ref",
    value.finalTranscriptMatrixEventRef,
  );
  addOptional(
    record,
    "final_transcript_timeline_readback_ref",
    value.finalTranscriptTimelineReadbackRef,
  );
  addOptional(
    record,
    "final_transcript_source_event_id",
    value.finalTranscriptSourceEventId,
  );
  addOptional(
    record,
    "no_transcript_matrix_event_ref",
    value.noTranscriptMatrixEventRef,
  );
  addOptional(
    record,
    "no_transcript_timeline_readback_ref",
    value.noTranscriptTimelineReadbackRef,
  );
  addOptional(
    record,
    "no_transcript_source_event_id",
    value.noTranscriptSourceEventId,
  );
  addOptional(record, "no_transcript_reason", value.noTranscriptReason);
  return record;
}

function bargeInRecord(
  value: PrenigmaStreamingVoiceBargeInReadback,
): JsonObject {
  return {
    barge_in_at_ms: value.bargeInAtMs,
    obsolete_turn_cancelled_at_ms: value.obsoleteTurnCancelledAtMs,
    obsolete_turn_id: value.obsoleteTurnId,
    supersedes_turn_id: value.supersedesTurnId,
    obsolete_tts_stream_ref: value.obsoleteTtsStreamRef,
    barge_in_source_event_ref: value.bargeInSourceEventRef,
    barge_in_timeline_readback_ref: value.bargeInTimelineReadbackRef,
    tts_cancel_ack_ref: value.ttsCancelAckRef,
    obsolete_audio_suppressed: value.obsoleteAudioSuppressed,
    tts_cancel_acknowledged: value.ttsCancelAcknowledged,
    source_event_readback_verified: value.sourceEventReadbackVerified,
    timeline_readback_verified: value.timelineReadbackVerified,
  };
}

function addOptional(record: JsonObject, key: string, value?: string): void {
  if (value !== undefined && value.trim()) record[key] = value;
}

function requireIdentity(
  options: BuildPrenigmaStreamingVoiceTurnEvidenceOptions,
): void {
  for (const [key, value] of Object.entries({
    productId: options.productId,
    roomId: options.roomId,
    sessionId: options.sessionId,
    tenantId: options.tenantId,
    turnId: options.turnId,
    workspaceId: options.workspaceId,
    workflowId: options.workflowId,
  })) {
    requireText(value, key);
  }
}

function requireMonotonicPhases(
  options: BuildPrenigmaStreamingVoiceTurnEvidenceOptions,
): void {
  const checks: Array<[boolean, string]> = [
    [
      options.startedAtMs <= options.sttFirstPartialAtMs,
      "started <= stt_first_partial",
    ],
    [
      options.sttFirstPartialAtMs <= options.sttFinalAtMs,
      "stt_first_partial <= stt_final",
    ],
    [
      options.sttFirstPartialAtMs <= options.agentAckAtMs,
      "stt_first_partial <= agent_ack",
    ],
    [
      options.agentAckAtMs <= options.agentFinalAtMs,
      "agent_ack <= agent_final",
    ],
    [
      options.sttFinalAtMs <= options.agentFinalAtMs,
      "stt_final <= agent_final",
    ],
    [
      options.agentAckAtMs <= options.ttsFirstAudioAtMs,
      "agent_ack <= tts_first_audio",
    ],
    [
      options.ttsFirstAudioAtMs <= options.ttsCompletedAtMs,
      "tts_first_audio <= tts_completed",
    ],
  ];
  for (const [valid, label] of checks) {
    if (!valid)
      throw new Error(`invalid streaming voice phase order: ${label}`);
  }
}

function requireProviderResolution(
  value: PrenigmaStreamingVoiceProviderResolution,
  role: "stt" | "tts",
): void {
  requireText(value.provider, `${role}.provider`);
  requireText(value.model, `${role}.model`);
  requireText(value.credentialRef, `${role}.credentialRef`);
  requireText(value.language, `${role}.language`);
  requireText(value.dataResidency, `${role}.dataResidency`);
  requireText(value.auditRef, `${role}.auditRef`);
  if (value.policyChecked !== true) {
    throw new Error(`${role}.policyChecked must be true`);
  }
  if (role === "tts") requireText(value.voiceId ?? "", "tts.voiceId");
}

function requireLiveProof(
  value: BuildPrenigmaStreamingVoiceTurnEvidenceOptions["liveProof"],
): void {
  if (value === undefined || value === null || typeof value !== "object") {
    throw new Error("liveProof is required");
  }
  for (const [key, flag] of Object.entries({
    authenticatedLiveSource: value.authenticatedLiveSource,
    incrementalAgentDispatchObserved: value.incrementalAgentDispatchObserved,
    matrixTimelineReadbackVerified: value.matrixTimelineReadbackVerified,
    sourceEventReadbackVerified: value.sourceEventReadbackVerified,
    streamingSttPartialObserved: value.streamingSttPartialObserved,
    streamingTtsAudioObserved: value.streamingTtsAudioObserved,
  })) {
    if (flag !== true) throw new Error(`liveProof.${key} must be true`);
  }
}

function requireEvidenceProvenance(
  value: PrenigmaStreamingVoiceEvidenceProvenance,
): void {
  if (value === undefined || value === null || typeof value !== "object") {
    throw new Error("evidenceProvenance is required");
  }
  requireText(value.sourceKind, "evidenceProvenance.sourceKind");
  requireText(value.producer, "evidenceProvenance.producer");
  requireText(value.hostSlot, "evidenceProvenance.hostSlot");
  requireText(value.sourceOfTruth, "evidenceProvenance.sourceOfTruth");
  if (
    ![
      PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
      "batch_voice_turn_probe",
      "external_runtime_adapter",
      "live_browser_harness",
    ].includes(value.sourceKind)
  ) {
    throw new Error("evidenceProvenance.sourceKind is not supported");
  }
  if (
    value.sourceKind === PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE &&
    value.continuousCallMediaObserved !== true
  ) {
    throw new Error(
      "evidenceProvenance.continuousCallMediaObserved must be true",
    );
  }
  if (
    value.sourceKind === PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE &&
    value.derivedFromBatchVoiceTurn === true
  ) {
    throw new Error(
      "evidenceProvenance.derivedFromBatchVoiceTurn must be false",
    );
  }
}

function requireContinuousMediaObservation(
  provenance: PrenigmaStreamingVoiceEvidenceProvenance,
  value: PrenigmaContinuousMediaObservationReadback | undefined,
): void {
  if (
    provenance.sourceKind !== PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE
  ) {
    return;
  }
  if (value === undefined || value === null || typeof value !== "object") {
    throw new Error("continuousMediaObservation is required");
  }
  requireText(value.mode, "continuousMediaObservation.mode");
  requireText(value.binding, "continuousMediaObservation.binding");
  requireText(
    value.sourceEventRef,
    "continuousMediaObservation.sourceEventRef",
  );
  requireText(
    value.receiptEventId,
    "continuousMediaObservation.receiptEventId",
  );
  requireText(
    value.trackReadyState,
    "continuousMediaObservation.trackReadyState",
  );
  const checks: Array<[boolean, string]> = [
    [value.status === "proved", "status must be proved"],
    [value.proved === true, "proved must be true"],
    [
      value.continuousCallMediaObserved === true,
      "continuousCallMediaObserved must be true",
    ],
    [
      value.sourceEventReadbackVerified === true,
      "sourceEventReadbackVerified must be true",
    ],
    [
      value.sourceEnvelopeSignatureVerified === true,
      "sourceEnvelopeSignatureVerified must be true",
    ],
    [
      value.evidenceSourceKind ===
        PRENIGMA_CONTINUOUS_CALL_MEDIA_EVIDENCE_SOURCE,
      "evidenceSourceKind must be continuous_call_media_runtime",
    ],
    [value.hostSlot === "streamingVoiceEvidence", "hostSlot is invalid"],
    [value.mediaKind === "audio", "mediaKind must be audio"],
    [value.mediaRuntime === "livekit", "mediaRuntime must be livekit"],
    [
      value.producer === "element_call_collaboration_runtime",
      "producer must be element_call_collaboration_runtime",
    ],
    [value.trackSource === "microphone", "trackSource must be microphone"],
    [value.trackReadyState !== "ended", "trackReadyState must not be ended"],
    [value.browserHarness === false, "browserHarness must be false"],
    [
      value.derivedFromBatchVoiceTurn === false,
      "derivedFromBatchVoiceTurn must be false",
    ],
    [value.rawAudioReturned === false, "rawAudioReturned must be false"],
    [
      value.rawTranscriptReturned === false,
      "rawTranscriptReturned must be false",
    ],
    [
      value.providerCredentialsReturned === false,
      "providerCredentialsReturned must be false",
    ],
    [
      value.rawEventContentReturned === false,
      "rawEventContentReturned must be false",
    ],
    [
      value.rawSecretMaterialReturned === false,
      "rawSecretMaterialReturned must be false",
    ],
    [
      Array.isArray(value.blockers) && value.blockers.length === 0,
      "blockers must be empty",
    ],
  ];
  for (const [valid, message] of checks) {
    if (!valid) throw new Error(`continuousMediaObservation.${message}`);
  }
}

function requireRoomContext(value: PrenigmaStreamingVoiceRoomContext): void {
  requireText(
    value.agentTurnMatrixEventRef,
    "roomContext.agentTurnMatrixEventRef",
  );
  requireText(
    value.agentTurnTimelineReadbackRef,
    "roomContext.agentTurnTimelineReadbackRef",
  );
  requireText(value.agentResponseRef, "roomContext.agentResponseRef");
  if (value.transcriptPersistence === "no_transcript") {
    requireText(
      value.noTranscriptMatrixEventRef ?? "",
      "roomContext.noTranscriptMatrixEventRef",
    );
    requireText(
      value.noTranscriptTimelineReadbackRef ?? "",
      "roomContext.noTranscriptTimelineReadbackRef",
    );
    requireText(
      value.noTranscriptSourceEventId ?? "",
      "roomContext.noTranscriptSourceEventId",
    );
    requireText(
      value.noTranscriptReason ?? "",
      "roomContext.noTranscriptReason",
    );
    return;
  }
  requireText(
    value.finalTranscriptMatrixEventRef ?? "",
    "roomContext.finalTranscriptMatrixEventRef",
  );
  requireText(
    value.finalTranscriptTimelineReadbackRef ?? "",
    "roomContext.finalTranscriptTimelineReadbackRef",
  );
  requireText(
    value.finalTranscriptSourceEventId ?? "",
    "roomContext.finalTranscriptSourceEventId",
  );
}

function requireBargeIn(value: PrenigmaStreamingVoiceBargeInReadback): void {
  requireText(value.obsoleteTurnId, "bargeIn.obsoleteTurnId");
  requireText(value.supersedesTurnId, "bargeIn.supersedesTurnId");
  requireText(value.obsoleteTtsStreamRef, "bargeIn.obsoleteTtsStreamRef");
  requireText(value.bargeInSourceEventRef, "bargeIn.bargeInSourceEventRef");
  requireText(
    value.bargeInTimelineReadbackRef,
    "bargeIn.bargeInTimelineReadbackRef",
  );
  requireText(value.ttsCancelAckRef, "bargeIn.ttsCancelAckRef");
  if (value.obsoleteTurnCancelledAtMs < value.bargeInAtMs) {
    throw new Error("bargeIn cancellation must follow barge-in observation");
  }
  for (const [key, flag] of Object.entries({
    obsoleteAudioSuppressed: value.obsoleteAudioSuppressed,
    sourceEventReadbackVerified: value.sourceEventReadbackVerified,
    timelineReadbackVerified: value.timelineReadbackVerified,
    ttsCancelAcknowledged: value.ttsCancelAcknowledged,
  })) {
    if (flag !== true) throw new Error(`bargeIn.${key} must be true`);
  }
}

function rejectRawMaterial(value: unknown, path: string): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      rejectRawMaterial(item, `${path}[${index}]`),
    );
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    const normalized = key.trim().toLowerCase();
    if (RAW_MATERIAL_KEYS.has(normalized)) {
      throw new Error(
        `raw streaming voice material is not allowed at ${path}.${key}`,
      );
    }
    rejectRawMaterial(item, `${path}.${key}`);
  }
}

function requireText(value: string, path: string): void {
  if (!value.trim()) throw new Error(`${path} is required`);
}
